import { useMemo } from "react";
import { Link } from "@/lib/router-compat";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import type { SolutionData } from "@/data/solutionsData";
import { useLanguage } from "@/hooks/useLanguage";
import { localizeHref } from "@/lib/localizeHref";
import { uiT } from "@/i18n/ui";
import type { UIKey } from "@/i18n/ui";

type RelatedItem = {
  slug: string;
  title: string;
  summary: string | null;
  featured_image: string | null;
  published_at?: string | null;
  type: "case-study" | "blog" | "white-paper" | "webinar" | "regulatory-update" | "news" | "podcast";
};

interface Props {
  solution: SolutionData;
}

const typeLabelMap: Record<string, UIKey> = {
  "case-study": "caseStudyLabel",
  "white-paper": "whitePaperLabel",
  "webinar": "webinarLabel",
  "regulatory-update": "regulatoryUpdateLabel",
  "news": "newsLabel",
  "podcast": "podcastLabel",
  "blog": "blogLabel",
};

const typeLinkPath = (type: string, slug: string, lang: import("@/hooks/useLanguage").SiteLang) => {
  const map: Record<string, string> = {
    "case-study": `/en/case-studies/${slug}`,
    "white-paper": `/en/downloads/${slug}`,
    "webinar": `/en/webinars/${slug}`,
    "regulatory-update": `/en/regulatory-updates/${slug}`,
    "news": `/en/news/${slug}`,
    "podcast": `/en/podcasts/${slug}`,
  };
  return localizeHref(map[type] || `/en/blog/${slug}`, lang);
};

const SolutionRelatedInsights = ({ solution }: Props) => {
  const lang = useLanguage();
  const t = uiT(lang);

  const { data: relatedContent = [] } = useQuery<RelatedItem[]>({
    queryKey: ["solution-related", solution.slug, solution.contentTags],
    queryFn: async () => {
      if (solution.curatedContent && solution.curatedContent.length > 0) {
        const webinarSlugs = solution.curatedContent.filter(c => c.type === "webinar").map(c => c.slug);
        const blogSlugs = solution.curatedContent.filter(c => c.type === "blog").map(c => c.slug);
        const caseSlugs = solution.curatedContent.filter(c => c.type === "case-study").map(c => c.slug);
        const wpSlugs = solution.curatedContent.filter(c => c.type === "white-paper").map(c => c.slug);

        const results: RelatedItem[] = [];

        if (webinarSlugs.length > 0) {
          // Webinars currently only exist in EN — hide in zh-cn
          if (lang !== "zh-cn") {
            const { data } = await supabase.from("webinars").select("slug, title, summary, featured_image").not("published_at", "is", null).in("slug", webinarSlugs);
            if (data) results.push(...data.map(w => ({ ...w, type: "webinar" as const })));
          }
        }
        if (blogSlugs.length > 0) {
          const blogLang = lang === "es" ? "es" : lang === "zh-cn" ? "zh-cn" : "en";
          const { data } = await supabase.from("blog_posts").select("slug, title, summary, featured_image").not("published_at", "is", null).eq("lang", blogLang).in("slug", blogSlugs);
          if (data) results.push(...data.map(b => ({ ...b, type: "blog" as const })));
        }
        if (caseSlugs.length > 0) {
          const cols = lang === "zh-cn"
            ? "slug, title, summary, featured_image, title_zh, summary_zh"
            : "slug, title, summary, featured_image";
          const { data } = await supabase.from("case_studies").select(cols).not("published_at", "is", null).in("slug", caseSlugs);
          if (data) {
            const filtered: any[] = lang === "zh-cn"
              ? data.filter((c: any) => c.title_zh).map((c: any) => ({ slug: c.slug, title: c.title_zh, summary: c.summary_zh ?? c.summary, featured_image: c.featured_image }))
              : data;
            results.push(...filtered.map((c: any) => ({ ...c, type: "case-study" as const })));
          }
        }
        if (wpSlugs.length > 0) {
          const cols = lang === "zh-cn"
            ? "slug, title, summary, featured_image, title_zh, summary_zh"
            : "slug, title, summary, featured_image";
          const { data } = await supabase.from("white_papers").select(cols).not("published_at", "is", null).in("slug", wpSlugs);
          if (data) {
            const filtered: any[] = lang === "zh-cn"
              ? data.filter((w: any) => w.title_zh).map((w: any) => ({ slug: w.slug, title: w.title_zh, summary: w.summary_zh ?? w.summary, featured_image: w.featured_image }))
              : data;
            results.push(...filtered.map((w: any) => ({ ...w, type: "white-paper" as const })));
          }
        }

        return solution.curatedContent
          .map(cc => results.find(r => r.slug === cc.slug && r.type === cc.type))
          .filter(Boolean) as RelatedItem[];
      }

      if (solution.contentTags && solution.contentTags.length > 0) {
        const tables = [
          { table: "blog_posts" as const, type: "blog" as const },
          { table: "case_studies" as const, type: "case-study" as const },
          { table: "white_papers" as const, type: "white-paper" as const },
          { table: "webinars" as const, type: "webinar" as const },
          { table: "regulatory_updates" as const, type: "regulatory-update" as const },
          { table: "company_news" as const, type: "news" as const },
          { table: "podcasts" as const, type: "podcast" as const },
        ];

        const queries = tables.map(async ({ table, type }) => {
          const isBlog = table === "blog_posts";
          const hasEs = !isBlog;
          const cols = "slug, title, summary, featured_image, published_at" + (hasEs ? ", title_es, summary_es" : "");
          let query = (supabase as any)
            .from(table)
            .select(cols)
            .not("published_at", "is", null)
            .order("published_at", { ascending: false })
            .limit(6);
          if (isBlog) {
            query = query.eq("lang", lang === "es" ? "es" : "en");
          }
          for (const tag of solution.contentTags!) {
            query = query.contains("tags", [tag]);
          }
          const { data } = await query;
          return (data ?? []).map((item: any) => {
            const localized = hasEs && lang === "es" ? {
              ...item,
              title: item.title_es || item.title,
              summary: item.summary_es ?? item.summary,
            } : item;
            return { ...localized, type };
          });
        });

        const results = (await Promise.all(queries)).flat();
        results.sort((a, b) => {
          const da = a.published_at ? new Date(a.published_at).getTime() : 0;
          const db = b.published_at ? new Date(b.published_at).getTime() : 0;
          return db - da;
        });

        return results.slice(0, 8);
      }

      const keywords = solution.title.toLowerCase().split(/\s+/).filter(w => w.length > 4);

      const { data: cases } = await supabase
        .from("case_studies")
        .select("slug, title, summary, featured_image")
        .not("published_at", "is", null)
        .order("published_at", { ascending: false })
        .limit(6);

      const { data: blogs } = await supabase
        .from("blog_posts")
        .select("slug, title, summary, featured_image")
        .not("published_at", "is", null)
        .eq("lang", lang === "es" ? "es" : "en")
        .order("published_at", { ascending: false })
        .limit(6);

      return [
        ...(cases || []).map(c => ({ ...c, type: "case-study" as const })),
        ...(blogs || []).map(b => ({ ...b, type: "blog" as const })),
      ]
        .map(item => {
          const t = item.title.toLowerCase();
          const score = keywords.filter(k => t.includes(k)).length;
          return { ...item, score };
        })
        .sort((a, b) => b.score - a.score)
        .slice(0, 3);
    },
    staleTime: 5 * 60 * 1000,
  });

  const viewAllLink = useMemo(() => {
    if (relatedContent.length === 0) return localizeHref("/en/knowledge-center", lang);
    const counts: Record<string, number> = {};
    relatedContent.forEach(item => {
      counts[item.type] = (counts[item.type] || 0) + 1;
    });
    const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
    const map: Record<string, string> = {
      "case-study": "/en/case-studies",
      "webinar": "/en/webinars",
      "regulatory-update": "/en/regulatory-updates",
      "white-paper": "/en/downloads",
    };
    return localizeHref(map[top] || "/en/blog", lang);
  }, [relatedContent, lang]);

  const externalItems = solution.externalRelated || [];

  if (relatedContent.length === 0 && externalItems.length === 0) return null;

  return (
    <section className="py-14 sm:py-20">
      <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
        {solution.relatedInsightsTitle ? (
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">{solution.relatedInsightsTitle}</h2>
            {solution.relatedInsightsSubtitle && (
              <p className="text-muted-foreground max-w-3xl mx-auto leading-relaxed">{solution.relatedInsightsSubtitle}</p>
            )}
          </div>
        ) : (
          <div className="flex items-end justify-between mb-8">
            <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">{t("relatedInsights")}</p>
            <Link
              to={viewAllLink}
              className="text-sm font-medium text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
            >
              {t("viewAll")} <ArrowRight size={13} />
            </Link>
          </div>
        )}
        <Carousel opts={{ align: "start", loop: false }} className="w-full">
          <CarouselContent className="-ml-5">
            {externalItems.map((ext, i) => (
              <CarouselItem key={`ext-${i}`} className="pl-5 basis-[280px] sm:basis-[320px]">
                <a
                  href={ext.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block"
                >
                  {ext.image && (
                    <div className="aspect-[3/2] rounded-xl overflow-hidden bg-muted mb-3">
                      <img
                        src={ext.image}
                        alt={ext.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                    {ext.label}
                  </span>
                  <h3 className="text-sm font-semibold text-foreground mt-1 line-clamp-2 group-hover:text-primary transition-colors duration-200">
                    {ext.title}
                  </h3>
                </a>
              </CarouselItem>
            ))}
            {relatedContent.map((item) => (
              <CarouselItem key={`${item.type}-${item.slug}`} className="pl-5 basis-[280px] sm:basis-[320px]">
                <Link
                  to={typeLinkPath(item.type, item.slug, lang)}
                  className="group block"
                >
                  {item.featured_image && (
                    <div className="aspect-[3/2] rounded-xl overflow-hidden bg-muted mb-3">
                      <img
                        src={item.featured_image}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                    {t(typeLabelMap[item.type] || "blogLabel")}
                  </span>
                  <h3 className="text-sm font-semibold text-foreground mt-1 line-clamp-2 group-hover:text-primary transition-colors duration-200">
                    {item.title}
                  </h3>
                </Link>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="flex justify-end gap-2 mt-6">
            <CarouselPrevious className="static translate-y-0 h-8 w-8" />
            <CarouselNext className="static translate-y-0 h-8 w-8" />
          </div>
        </Carousel>
      </div>
    </section>
  );
};

export default SolutionRelatedInsights;
